import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  EmptyState,
  InlineMessage,
} from '../../components';
import type { ProfileCandidate } from '../../types/api';
import { CONFIDENCE_LABELS, CONFIDENCE_TONE, evidenceLabel, isNegative } from './evidence-labels';

export interface ProfileCandidateListProps {
  candidates: ProfileCandidate[];
  tiedAtTop: number;
  submitting: boolean;
  error: string | null;
  onConfirm: (profileIds: string[], save: boolean) => void;
}

/**
 * Apresentação dos candidatos (SPEC-005 §7).
 *
 * Nada aqui vem pré-selecionado, nem com confiança alta (RF-D04). A tela
 * apresenta candidatos e evidências; quem decide identidade é quem pesquisa.
 */
export function ProfileCandidateList({
  candidates,
  tiedAtTop,
  submitting,
  error,
  onConfirm,
}: ProfileCandidateListProps) {
  const [chosen, setChosen] = useState<string[]>([]);
  const [save, setSave] = useState(false);

  if (candidates.length === 0) {
    return (
      <EmptyState
        title="Nenhum perfil encontrado"
        description="Tente informar o nome de usuário ou o endereço do perfil — a busca por nome depende de o perfil ser público e encontrável."
      />
    );
  }

  const toggle = (profileId: string): void => {
    setChosen((current) =>
      current.includes(profileId)
        ? current.filter((id) => id !== profileId)
        : [...current, profileId],
    );
  };

  return (
    <Card>
      <CardHeader
        title="Qual destes perfis você quer pesquisar?"
        description="A plataforma não identifica pessoas: ela mostra o que encontrou e a razão de cada correspondência. A coleta só começa depois da sua escolha."
      />

      {/* Empate técnico: a posição na lista não pode comunicar uma diferença
          que não existe (RF-D15). */}
      {tiedAtTop > 1 && (
        <InlineMessage tone="warning">
          Os {tiedAtTop} primeiros perfis são igualmente prováveis. A ordem entre eles não indica
          preferência — compare as evidências antes de escolher.
        </InlineMessage>
      )}

      {error && <InlineMessage tone="danger">{error}</InlineMessage>}

      <ul className="flex flex-col gap-3">
        {candidates.map((candidate) => (
          <li key={candidate.profileId}>
            <CandidateRow
              candidate={candidate}
              checked={chosen.includes(candidate.profileId)}
              onToggle={() => toggle(candidate.profileId)}
            />
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <Checkbox
          label="Guardar os perfis escolhidos para pesquisas futuras"
          checked={save}
          onChange={() => setSave((value) => !value)}
        />
        <Button
          disabled={chosen.length === 0}
          loading={submitting}
          onClick={() => onConfirm(chosen, save)}
        >
          {chosen.length <= 1 ? 'Coletar deste perfil' : `Coletar destes ${chosen.length} perfis`}
        </Button>
      </div>
    </Card>
  );
}

function CandidateRow({
  candidate,
  checked,
  onToggle,
}: {
  candidate: ProfileCandidate;
  checked: boolean;
  onToggle: () => void;
}) {
  const positives = candidate.evidence.filter((item) => !isNegative(item));
  const negatives = candidate.evidence.filter(isNegative);

  return (
    <div className="flex gap-3 rounded-lg border border-border bg-surface p-4">
      <Checkbox
        label=""
        checked={checked}
        disabled={!candidate.isCollectable}
        onChange={onToggle}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{candidate.displayName ?? candidate.username}</span>
          {candidate.username && <span className="text-sm text-muted">@{candidate.username}</span>}
          {candidate.isVerified === true && <Badge tone="info">Verificada</Badge>}
          <Badge tone={CONFIDENCE_TONE[candidate.confidence]}>
            {CONFIDENCE_LABELS[candidate.confidence]}
          </Badge>
        </div>

        {candidate.bio && <p className="line-clamp-2 text-sm text-muted">{candidate.bio}</p>}

        <p className="text-sm text-muted">
          {candidate.followers !== null &&
            `${candidate.followers.toLocaleString('pt-BR')} seguidores`}
          {candidate.followers !== null && candidate.postsCount !== null && ' · '}
          {candidate.postsCount !== null &&
            `${candidate.postsCount.toLocaleString('pt-BR')} publicações`}
        </p>

        {/* O "porquê" da confiança, em português (RF-D02, §6). */}
        <p className="text-sm">
          <span className="text-muted">Por que apareceu: </span>
          {positives.map(evidenceLabel).join('; ') || 'correspondência fraca'}
        </p>

        {negatives.length > 0 && (
          <p className="text-sm text-warning">
            <span className="text-muted">Atenção: </span>
            {negatives.map(evidenceLabel).join('; ')}
          </p>
        )}

        {/* Perfil privado é candidato não coletável, não erro (RF-D10). */}
        {!candidate.isCollectable && (
          <InlineMessage tone="info">
            Este perfil é privado. A plataforma só coleta conteúdo público, então ele não pode ser
            pesquisado.
          </InlineMessage>
        )}

        <a
          className="text-sm text-accent underline"
          href={candidate.profileUrl}
          target="_blank"
          rel="noreferrer noopener"
        >
          Abrir perfil na plataforma
        </a>
      </div>
    </div>
  );
}
